import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@lsq/nextgen/tooltip';
import { ReactComponent as ChevronDownIcon } from '../../assets/svgIcons/lsq_chevron_down.svg';
import Chip from '../Chip';
import styles from './FilterTrigger.module.css';

export const TRIGGER_STATE = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DISABLED: 'disabled'
};

/**
 * Derive trigger state from dropdown open + disabled flags. Feature-specific
 * filter wrappers (StatusFilter, etc.) call this to convert their local state
 * into the trigger's visual state enum.
 */
export const getTriggerState = (isOpen, disabled = false) => {
  if (disabled) return TRIGGER_STATE.DISABLED;
  if (isOpen) return TRIGGER_STATE.ACTIVE;
  return TRIGGER_STATE.INACTIVE;
};

/**
 * Filter-style trigger: wraps Chip with active/inactive/disabled state and
 * the onClear event-isolation logic. Port of swlite's Trigger.tsx.
 *
 * - When `onClear` is provided AND there's at least one selection, the
 *   IconButton renders as a close × that calls onClear on pointerDown (which
 *   fires before the DropdownRoot's click listener, preventing the open-toggle
 *   from firing on the same click).
 * - Otherwise the IconButton renders the chevron-down arrow, which rotates
 *   180° when the dropdown is open (active state).
 */
const FilterTrigger = React.forwardRef(function FilterTrigger(
  {
    label,
    selectedOptions = [],
    triggerState = TRIGGER_STATE.INACTIVE,
    onClear,
    disableTooltip = '',
    hideIcon = false,
    ...rest
  },
  ref
) {
  const isActive = triggerState === TRIGGER_STATE.ACTIVE;
  const isDisabled = triggerState === TRIGGER_STATE.DISABLED;
  const isCloseIcon = Boolean(onClear && selectedOptions?.length);

  const handleCrossClick = (event) => {
    if (!isCloseIcon) return;
    event.stopPropagation();
    event.preventDefault();
    onClear?.();
  };

  const getSubLabel = () => {
    if (!selectedOptions?.length) return null;
    const first = selectedOptions[0].label;
    const overflow = selectedOptions.length > 1 ? `+${selectedOptions.length - 1}` : '';
    return (
      <>
        <span
          className={styles.trigger_card_text}
          title={selectedOptions.map((o) => o.label).join(', ')}>
          {first}
        </span>
        {overflow ? <span>{` ${overflow}`}</span> : null}
      </>
    );
  };

  const triggerClass = [
    isActive ? styles.trigger_active : styles.trigger_inactive,
    isDisabled ? styles.trigger_disable : '',
    selectedOptions?.length ? styles.trigger_selected : ''
  ]
    .filter(Boolean)
    .join(' ');

  const customIcon = !isCloseIcon ? (
    <ChevronDownIcon
      className={`${styles.arrow_icon} ${
        isActive ? styles.arrow_icon_active : ''
      } ${isDisabled ? styles.arrow_icon_disable : ''}`}
    />
  ) : undefined;

  const chip = (
    <Chip
      ref={ref}
      className={triggerClass}
      label={label}
      subLabel={getSubLabel()}
      customIcon={customIcon}
      onCrossClick={handleCrossClick}
      onButtonClick={() => {}}
      hideIcon={hideIcon}
      {...rest}
    />
  );

  if (!disableTooltip) return chip;
  return <Tooltip content={disableTooltip}>{chip}</Tooltip>;
});

FilterTrigger.propTypes = {
  label: PropTypes.string.isRequired,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
  ),
  triggerState: PropTypes.oneOf(Object.values(TRIGGER_STATE)),
  onClear: PropTypes.func,
  disableTooltip: PropTypes.string,
  hideIcon: PropTypes.bool
};

export default FilterTrigger;
